// PaymentReport.js

import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Tooltip,
  Spinner,
  useTheme,
  useColorMode,
  Select,
  Input,
  Button,
  Link,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import dialogManager from "../DialogManager/DialogManager";
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

import {
  faArrowDown,
  faArrowUp,
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlassDollar,
} from "@fortawesome/free-solid-svg-icons";
import { cqAPI } from "../../lib/Auth";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./PaymentReport.css";
import { useReactToPrint } from 'react-to-print';

function PaymentReport() {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState("Today");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [refundRequest ,setRefundRequest] = useState({});
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const [phoneNumber, setPhoneNumber] = useState("");
  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [startDate, endDate] = dateRange;
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const sortedTransactions = useMemo(() => {
    let sortableItems = [...payments];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const va =
          sortConfig.key === "created"
            ? new Date(a[sortConfig.key])
            : a[sortConfig.key];
        const vb =
          sortConfig.key === "created"
            ? new Date(b[sortConfig.key])
            : b[sortConfig.key];
        if (va < vb) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (va > vb) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [payments, sortConfig]);
  const showLocationColumn = sortedTransactions.some((payment) => payment.location_name);

  const fetchPayments = async (params = {}) => {
    const { startDate, endDate } = params;
    setIsLoading(true);

    try {
      // Use parameters if provided, otherwise fallback to component state
      const formattedStartDate = startDate
        ? moment(startDate).format("L")
        : moment(dateRange[0]).format("L");
      const formattedEndDate = endDate
        ? moment(endDate).format("L")
        : moment(dateRange[1]).format("L");

      const response = await cqAPI.get(
        `/reports/payments?rangeBeg=${formattedStartDate}&rangeEnd=${formattedEndDate}`
      );

      if (response.type === "ok"){
        setPayments(response.data);
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    let start, end;
    switch (value) {
      case "Today":
        start = moment().startOf("day");
        end = moment().endOf("day");
        break;
      case "Yesterday":
        start = moment().subtract(1, "days").startOf("day");
        end = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        start = moment().subtract(6, "days").startOf("day");
        end = moment().endOf("day");
        break;
      case "Last 4 weeks":
        start = moment().subtract(4, "weeks").startOf("day");
        end = moment().endOf("day");
        break;
      case "Month to Date":
        start = moment().startOf("month");
        end = moment().endOf("day");
        break;
      case "Quarter to date":
        start = moment().startOf("quarter");
        end = moment().endOf("day");
        break;
      case "Year to date":
        start = moment().startOf("year");
        end = moment().endOf("day");
        break;
      case "Custom":
      default:
        start = null;
        end = null;
    }
    setDateRange([start ? start.toDate() : null, end ? end.toDate() : null]);
  };
  
  const updateUrlHash = (
    startDate,
    endDate,
    selectedRange
  ) => {
    const queryParams = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
    }).toString();
    window.location.hash = queryParams;
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    monthDate,
  }) => (
    <Flex
      direction="column"
      width="100%"
      paddingLeft="20px"
      paddingRight="20px"
    >
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box
          p={2}
          borderRadius="md"
          borderWidth="2px"
          borderColor="purple.500"
          mr={2}
        >
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingTop="10px"
      >
        <Box
          as="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>
          {date.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </Box>
        <Box
          as="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1)); // Remove the '#' part
    const startDate = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().startOf("day").toDate();
    const endDate = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).toDate()
      : moment().endOf("day").toDate();
    const selectedRange = hashParams.get("selectedRange") || "Today";

    setDateRange([startDate, endDate]);
    setSelectedRange(selectedRange);

    fetchPayments({
      startDate,
      endDate,
      selectedRange,
    });
  }, []);

  useEffect(() => {
    updateUrlHash(startDate, endDate, selectedRange);
  }, [startDate, endDate, selectedRange]);

  if (isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
        position="relative"
      >
        <Spinner className="centered-spinner" color="blue.500" size="xl" />
      </Flex>
    );
  }
  
  const handleRefund = async ()=>{
    
    if (
      !refundRequest.amount ||
      isNaN(parseFloat(refundRequest.refund_amount)) ||
      parseFloat(parseFloat(refundRequest.refund_amount).toFixed(2)) !==
      parseFloat(refundRequest.refund_amount) ||
      !refundRequest.description ||
      refundRequest.description.trim() === ""
    ) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: "Please enter a valid amount (up to 2 decimal places) and description.",
        header: "Error",
      });
      return;
    }
    
    if (refundRequest.amount < (refundRequest.refund_amount * 100) ){
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: "The refund can only be up to the full amount of $" +(refundRequest.amount / 100).toFixed(2) + '.',
        header: "Error",
      });
      return;
    }
    
    const response = await cqAPI.post('/payments/refund',{
      id : refundRequest.refund_id,
      amount : refundRequest.refund_amount,
      desc : refundRequest.description
    });
    if (response.type === 'ok'){
      setRefundRequest({});
      onClose();
      fetchPayments();
      return;
    }
    if (response.type === 'error' && response.msg){
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    }
  };

  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justifyContent="flex-start" mb={4}>
        <Flex align="center" mr={2}>
          <Select
            placeholder="Select range"
            value={selectedRange}
            onChange={(e) => handleDateRangeSelection(e.target.value)}
            className="date-type"
          >
            <option value="Today">Today</option>
            <option value="Yesterday">Yesterday</option>
            <option value="Last 7 days">Last 7 days</option>
            <option value="Last 4 weeks">Last 4 weeks</option>
            <option value="Month to Date">Month to Date</option>
            <option value="Quarter to date">Quarter to date</option>
            <option value="Year to date">Year to date</option>
            <option value="Custom">Custom</option>
          </Select>
        </Flex>
        <Flex align="center" mr={2}>
          <DatePicker
            className={`custom-datepicker ${
              colorMode === "light" ? "light-theme" : "dark-theme"
            }`}
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => {
              setDateRange(update);
              setSelectedRange("Custom"); // Ensure custom range is selected when dates are manually changed
            }}
            isClearable={true}
            renderCustomHeader={renderCustomHeader}
          />
        </Flex>
        <Flex marginLeft="auto">
          <Button
            leftIcon={<FontAwesomeIcon icon={faMagnifyingGlassDollar} />}
            onClick={fetchPayments}
          >
            Show Payments
          </Button>
          <Button ml={2} onClick={handlePrint} className="no-print">Print</Button>
        </Flex>
      </Flex>

      <Flex data-report-download-target justify="flex-end" mb={2} className="no-print" />

      <div ref={componentRef}>
        <VStack spacing={0} align="stretch">
          <Flex borderBottom="1px" borderColor="gray.200" p={2}>
            <HStack spacing={8} flex="1">
              <Text
                fontSize="sm"
                flexBasis="20%"
                fontWeight="bold"
                onClick={() => requestSort("created")}
                cursor="pointer"
              >
                Date
              </Text>
              {showLocationColumn && (
                <Text fontSize="sm" flexBasis="15%" fontWeight="bold">
                  Location
                </Text>
              )}
              <Text
                fontSize="sm"
                flexBasis="20%"
                fontWeight="bold"
                onClick={() => requestSort("description")}
                cursor="pointer"
              >
                Description
              </Text>
              <Text
                fontSize="sm"
                flexBasis="15%"
                fontWeight="bold"
                onClick={() => requestSort("bosted_by")}
                cursor="pointer"
                className="no-print"
              >
                Posted By 
              </Text>
              <Text
                fontSize="sm"
                flexBasis="15%"
                fontWeight="bold"
                onClick={() => requestSort("patient")}
                cursor="pointer"
              >
                Patient
              </Text>
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("amount")}
                cursor="pointer"
              >
                Amount
              </Text>
              <Text
                fontSize="sm"
                flexBasis="15%"
                fontWeight="bold"
                onClick={() => requestSort("last4")}
                cursor="pointer"
              >
                Source
              </Text>
              <Text
                fontSize="sm"
                flexBasis="20%"
                fontWeight="bold"
                cursor="pointer"
                className="no-print"
              >
                Action
              </Text>
            </HStack>
          </Flex>
          {sortedTransactions.map((payment) => (
            <Flex
              key={payment._id}
              p={2}
            >
              <HStack spacing={8} flex="1">
                <Text fontSize="sm" flexBasis="20%">
                  {moment(payment.created).format("MMM D, YYYY h:mm a")}
                </Text>
                {showLocationColumn && (
                  <Text fontSize="sm" flexBasis="15%">
                    {payment.location_name || "-"}
                  </Text>
                )}
                <Text fontSize="sm" flexBasis="20%">
                  {payment.description}
                </Text>
                <Text fontSize="sm" flexBasis="15%" className="no-print">
                  {payment.posted_by}
                </Text>
                <Text fontSize="sm" flexBasis="15%">
                  {payment.patient}
                </Text>
                <Text fontSize="sm" flexBasis="10%">
                  {(-payment.amount / 100).toFixed(2)}
                </Text>
                <Text fontSize="sm" flexBasis="15%">
                  {payment.last4}
                </Text>
                <Text fontSize="sm" flexBasis="20%" className="no-print">
                  {payment.type === 'direct' && (
                    <Link onClick={()=>{
                      setRefundRequest({
                        ...refundRequest,
                        amount : -payment.amount,
                        refund_id : payment.refund_id
                      });
                      onOpen();
                    }}>
                      Refund / Partial Refund
                    </Link>
                  )}
                </Text>
              </HStack>
            </Flex>
          ))}
        </VStack>
        <Box textAlign="right">
          <Text>
            Total: ${
              (payments.reduce((acc, cur)=>{acc += -cur.amount; return acc},0) / 100).toFixed(2)
            }
          </Text>
        </Box>
      </div>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Refund</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text>
              You can refund the full amount of ${ (refundRequest.amount / 100).toFixed(2) } or issue a partial refund.
            </Text>
            <FormControl>
              <FormLabel>Refund Amount ($)</FormLabel>
              <Input
                placeholder="0.00"
                value={refundRequest.refund_amount}
                onChange={(e) =>
                  setRefundRequest({ ...refundRequest, refund_amount: e.target.value })
                }
                type="number"
                step="0.01"
              />
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Description</FormLabel>
              <Input
                placeholder="Description of refund"
                value={refundRequest.description}
                onChange={(e) =>
                  setRefundRequest({ ...refundRequest, description: e.target.value })
                }
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleRefund}
            >
              OK
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default PaymentReport;
